import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { AuthUserContext } from "../../context/AuthUserContext";
import AddressBookCheckout from "./AddressBookCheckout";
import { useTranslation } from "react-i18next";
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionBody,
  StepBadge,
  StepBadgeContent,
} from "../checkout/CheckoutSection";

const GridContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-sm);
  }
`;
const AddressOption = styled.div`
  position: relative;
  background-color: ${(props) =>
    props.$selected ? "var(--primary-300)" : "var(--bg-100)"};
  border: 2px solid
    ${(props) => (props.$selected ? "var(--primary-100)" : "var(--bg-200)")};
  border-radius: var(--border-radius-base);
  padding: var(--spacing-md);
  cursor: pointer;
  transition: border-color var(--transition-fast),
    background-color var(--transition-fast);

  &:hover {
    border-color: var(--primary-200);
  }
`;
const AddressTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-xs);
`;
const AddressTypeTag = styled.span`
  font-size: var(--font-size-small);
  text-transform: capitalize;
  color: var(--text-200);
  background-color: var(--bg-200);
  padding: 2px var(--spacing-sm);
  border-radius: var(--border-radius-large);
`;
const RadioOuter = styled.div`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${(props) => (props.$selected ? "var(--primary-100)" : "var(--bg-300)")};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const RadioInner = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: var(--primary-100);
`;
const AddressText = styled.div`
  font-size: var(--font-size-small);
  color: var(--text-100);
  line-height: 1.5;

  p {
    margin: 0;
  }
`;
const AddressActions = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--bg-200);
`;
const GhostButton = styled.button`
  background-color: transparent;
  color: var(--text-200);
  border: none;
  padding: var(--spacing-xxs) var(--spacing-xs);
  font-size: var(--font-size-small);
  text-decoration: underline;

  &:hover {
    background-color: transparent;
    color: var(--primary-100);
  }
`;
const AddButton = styled.button`
  background-color: transparent;
  color: var(--primary-100);
  border: 1px dashed var(--primary-200);
  margin-top: var(--spacing-md);
  width: 100%;

  &:hover {
    background-color: var(--primary-300);
    color: var(--primary-100);
  }
`;

const ConfirmContainer = styled.div`
  margin-top: var(--spacing-xs);
  background-color: #00000031;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 9;
  max-width: 100vw;
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
`;
const ConfirmWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;

  background-color: var(--bg-200);
  position: relative;
  overflow: auto; /* omogućava skrolanje unutar div-a */
  -webkit-overflow-scrolling: touch;

  padding: 60px 0;
  width: 94%;
  border-radius: var(--border-radius-large);
  @media (min-width: 768px) {
    width: 500px;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xl);
`;

const ButtonTransparent = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);

  cursor: pointer;
  color: var(--primary-200);
  background-color: var(--bg-100);
  &:hover {
    color: var(--primary-100);
    background-color: var(--bg-200);
  }
`;
const ButtonConfirm = styled(ButtonTransparent)`
  color: var(--error-color);
  background-color: var(--bg-100);
  border-color: var(--error-color);
  &:hover {
    color: var(--bg-100);
    background-color: var(--error-color);
  }
`;

const AddressCardCheckout = ({ setAddress }) => {
  const { t } = useTranslation();
  const { userProfile, deleteAddressBook } = useContext(AuthUserContext);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [deleteAddress, setDeleteAddress] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Postavite primarnu adresu kao podrazumevano selektovanu pri učitavanju
  useEffect(() => {
    if (userProfile && userProfile.addresses) {
      const primaryAddress = userProfile.addresses.find(
        (address) => address.is_primary
      );
      setSelectedAddress(primaryAddress || userProfile.addresses[0]); // Ako nema primarne, uzmite prvu adresu
    }
  }, [userProfile]);

  const handleDelete = async (e, addressId) => {
    e.preventDefault();

    try {
      await deleteAddressBook(addressId);
    } catch (error) {
      console.error("Greška prilikom brisanja adrese:", error); // Obradite grešku
    }
    setShowConfirm(false);
    setDeleteAddress(null);
  };
  useEffect(() => {
    setAddress(selectedAddress);
  }, [selectedAddress]);

  if (showAddressForm) {
    return (
      <AddressBookCheckout
        showAddressForm={showAddressForm}
        setShowAddressForm={setShowAddressForm}
        editAddress={editAddress}
      />
    );
  }

  return (
    <SectionCard>
      <SectionHeader>
        <StepBadge $done={!!selectedAddress}>
          <StepBadgeContent step={1} done={!!selectedAddress} />
        </StepBadge>
        <SectionTitle>{t("ADDRESS_BOOK.SHIPPING_ADDRESS")}</SectionTitle>
      </SectionHeader>
      <SectionBody>
        <GridContainer>
          {userProfile?.addresses.map((address, index) => {
            const isSelected = selectedAddress?.id === address.id;
            return (
              <AddressOption
                key={index}
                $selected={isSelected}
                onClick={() => setSelectedAddress(address)}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedAddress(address);
                  }
                }}
              >
                <AddressTop>
                  <AddressTypeTag>{address.type}</AddressTypeTag>
                  <RadioOuter $selected={isSelected}>
                    {isSelected && <RadioInner />}
                  </RadioOuter>
                </AddressTop>
                <AddressText>
                  <p>
                    {userProfile?.first_name} {userProfile?.last_name}
                  </p>
                  <p>
                    {address.street} {address.street_number}
                  </p>
                  <p>
                    {address.postal_code} {address.city}, {address.country}
                  </p>
                  <p>Tel: {address.phone_number}</p>
                </AddressText>
                <AddressActions>
                  <GhostButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditAddress(address);
                      setShowAddressForm(true);
                    }}
                  >
                    {t("BUTTONS.EDIT")}
                  </GhostButton>
                  {userProfile?.addresses.length !== 1 &&
                    !address.is_primary && (
                      <GhostButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConfirm(true);
                          setDeleteAddress(address);
                        }}
                      >
                        {t("BUTTONS.DELETE")}
                      </GhostButton>
                    )}
                </AddressActions>
              </AddressOption>
            );
          })}
        </GridContainer>

        <AddButton
          onClick={() => {
            setShowAddressForm(true);
            setEditAddress(null);
          }}
        >
          {t("ADDRESS_BOOK.ADD_NEW_ADDRESS")}
        </AddButton>

        {showConfirm && (
          <ConfirmContainer>
            <ConfirmWrapper>
              {t("ADDRESS_BOOK.CONFIRM_DELETE")}
              <ButtonContainer>
                <ButtonConfirm
                  onClick={(e) => handleDelete(e, deleteAddress.id)}
                >
                  {t("BUTTONS.CONFIRM")}
                </ButtonConfirm>
                <ButtonTransparent
                  onClick={(e) => {
                    setShowConfirm(false);
                    setDeleteAddress(null);
                  }}
                >
                  {t("BUTTONS.CANCEL")}
                </ButtonTransparent>
              </ButtonContainer>
            </ConfirmWrapper>
          </ConfirmContainer>
        )}
      </SectionBody>
    </SectionCard>
  );
};

export default AddressCardCheckout;
